import { useEffect, useState } from 'react';
import { Mail, Trash2 } from 'lucide-react';
import { Pagination } from '@/components/ui';
import { formatDomainDisplay } from '@/lib/senderHelpers';
import { RequiredSenderBadge } from '@/components/domain/shared/RequiredSenderBadge';

export interface WalkthroughSenderListItem {
  id: string;
  type: 'domain' | 'email';
  value: string;
  /**
   * True for the user's own forwarding address (system_managed row).
   * In edit mode the row swaps the trash icon for a "Required" badge —
   * RLS prevents deleting it and removing it from the user list would
   * break Gmail's forward back to the Yaruta label.
   */
  required?: boolean;
}

export interface WalkthroughSenderListProps {
  items: WalkthroughSenderListItem[];
  /**
   * When `onRemove` is provided, each row renders a trash icon. Used in
   * the Step 1 Edit Senders sub-state. In read-only contexts (Step 1
   * default and Step 2 accordion) the prop is omitted.
   */
  onRemove?: (id: string) => void;
  /**
   * Override the page size. Defaults to 4 rows per page, matching the
   * walkthrough Figma (8662:73258 — "1/10").
   */
  pageSize?: number;
}

const DEFAULT_PAGE_SIZE = 4;

/**
 * Paginated approved-sender list used inside the YAR-238 walkthrough.
 * Optionally renders trash icons for edit-mode. Renders its own "Senders"
 * label + pagination control so callers don't reimplement it.
 *
 * Behaviour differs from the onboarding/Settings sender lists in two
 * ways: items are passed in (no DB query), and the Edit sub-state is
 * batch — the parent owns the in-memory mutation and only persists on
 * "Save changes".
 */
export function WalkthroughSenderList({
  items,
  onRemove,
  pageSize = DEFAULT_PAGE_SIZE,
}: WalkthroughSenderListProps) {
  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
  const [page, setPage] = useState(0);

  useEffect(() => {
    if (page > totalPages - 1) setPage(Math.max(0, totalPages - 1));
  }, [page, totalPages]);

  if (items.length === 0) {
    return (
      <div className="flex flex-col gap-2 w-full">
        <p className="text-sm font-medium text-foreground">Senders</p>
        <div
          role="status"
          aria-live="polite"
          className="rounded-xl bg-surface-secondary p-6 flex flex-col items-center justify-center gap-2 min-h-[180px]"
        >
          <Mail
            size={32}
            className="text-icon-default"
          />
          <p className="text-base font-medium text-foreground">
            Nothing here yet
          </p>
          <p className="text-sm text-muted-foreground">
            Start adding your senders in this list
          </p>
        </div>
      </div>
    );
  }

  const pageItems = items.slice(page * pageSize, (page + 1) * pageSize);

  return (
    <div className="flex flex-col gap-2 w-full">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-foreground">Senders</p>
        <Pagination
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          size="sm"
        />
      </div>

      <div className="flex flex-col gap-2">
        {pageItems.map((item) => {
          const display =
            item.type === 'domain'
              ? formatDomainDisplay(item.value)
              : item.value;
          return (
            <div
              key={item.id}
              className="flex gap-2 items-center border border-border rounded-xl px-4 py-3 bg-background"
            >
              <p className="flex-1 text-sm font-medium text-foreground leading-5 min-w-0 truncate">
                {display}
              </p>
              {onRemove &&
                (item.required ? (
                  <RequiredSenderBadge
                    ariaLabel={`${display} — required sender, used to accept forwarded emails`}
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => onRemove(item.id)}
                    className="w-8 h-8 flex items-center justify-center rounded-sm shrink-0"
                    aria-label={`Remove ${display}`}
                  >
                    <Trash2
                      size={20}
                      className="text-icon-default"
                    />
                  </button>
                ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}
